import { useParams } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getShipment, getDHLTrackingUrl } from '../../api/shipping'
import { DetailSection } from '@/components/DetailSection'
import { PageHeader } from '@/components/PageHeader'
import { StatusBadge } from '@/components/StatusBadge'
import { Button } from '@/components/ui/button'
import { ExternalLink } from 'lucide-react'

export default function ShippingDetail() {
  const { id } = useParams<{ id: string }>()

  const { data: shipment, isLoading, isError } = useQuery({
    queryKey: ['shipment', id],
    queryFn: () => getShipment(id!),
    enabled: !!id,
  })

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Lade Sendung…</div>
  if (isError || !shipment) return (
    <div className="p-6">
      <p className="text-sm text-destructive">Sendung nicht gefunden.</p>
    </div>
  )

  const trackingUrl = shipment.tracking_code ? getDHLTrackingUrl(shipment.tracking_code) : null

  return (
    <div className="p-6 max-w-2xl">
      <PageHeader
        title={shipment.tracking_code ?? `Sendung #${shipment.id}`}
        breadcrumb={[{ label: 'Versand', href: '/shipping' }]}
        actions={
          trackingUrl ? (
            <Button variant="outline" size="sm" asChild>
              <a href={trackingUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-1.5 size-4" />
                Tracking öffnen
              </a>
            </Button>
          ) : undefined
        }
      />
      <div className="mt-4 space-y-4">
        <DetailSection
          rows={[
            { label: 'Dienstleister', value: shipment.deliverer },
            { label: 'Sendungsnummer', value: shipment.tracking_code, copyable: true },
            { label: 'Status', value: <StatusBadge status={shipment.status} /> },
            {
              label: 'Versanddatum',
              value: shipment.shipped_at
                ? new Date(shipment.shipped_at).toLocaleDateString('de-DE')
                : undefined,
            },
            { label: 'Erstellt', value: new Date(shipment.created_at).toLocaleString('de-DE') },
          ].filter((r) => r.value !== undefined && r.value !== null)}
        />
        <div className="text-sm">
          <span className="text-muted-foreground">Auftrag: </span>
          {shipment.order_number ? <Link
            to={`/orders/${encodeURIComponent(shipment.order_number)}`}
            className="text-primary hover:underline font-mono"
          >
            {shipment.order_number}
          </Link> : <span className="font-mono">#{shipment.order_id}</span>}
        </div>
      </div>
    </div>
  )
}
